<template>
  <div class="ProductB2CLayout">
    <!-- * * * * * * * PRODUCT IMAGE AND BUY BOX -->
    <div
      class="desk:grid grid-cols-[80px_minmax(0,_1fr)_repeat(10,_minmax(0,_1fr))] gap-x-12
             mb-48 desk:mb-80 desk:pt-16 desk:layout-container"
    >
      <!-- spalt 1 -->
      <div class="hidden desk:block col-span-1">
        <div id="ProductImageThumbnails" class=" relative h-full max-h-[468px]" />
      </div>

      <!-- spalt 2 -->
      <div class="mobOnly:w-full mobOnly:mb-24 desk:col-span-6 text-left">
        <ProductImage
          :key="`productImage${activeVariant.partNo}`"
          :product-item="productItem"
          :current-image-list="currentImageList"
          :active-variant="activeVariant"
        />
      </div>

      <!-- spalt 3 -->
      <div class="mobOnly:w-full desk:col-span-5 desk:pl-28">
        <div class="mobOnly:px-12 ">
          <ProductBreadcrumbs v-if="uiStore.isMobile" :product-item="productItem" />

          <!-- * * * * * * name & category -->
          <B2CNameAndCategory
            :product-item="productItem"
            :active-variant="activeVariant"
          />

          <!-- * * * * * * price -->
          <PricePublic
            v-if="!globalContent.disablePricingOnB2CPage"
            :product-item="productItem"
            :active-variant="activeVariant"
            :display-inc-vat="b2cDisplayIncVat"
          />

          <!-- anchor link to related products (hidden when BuyBox is shown, as it has its own) -->
          <div v-if="hasRelatedProducts && !isLimitedContent && (!productItem.isD2C || hasPageTypeConflict)" class="mb-24">
            <a href="#pdpRelatedProducts" class="underline">
              {{ $t('pdp.link.alternative') }}
            </a>
          </div>
        </div>

        <!-- * * * * * * variant selector -->
        <VariantSelector
          :product-item="productItem"
          :active-variant="activeVariant"
          design="standard"
          @update-active-variant="(variant) => updateActiveVariant(variant)"
        />
        <div class="mobOnly:px-12">
          <!-- * * * * * * buy box for D2C Products -->
          <BuyBox
            v-if="productItem.isD2C && globalContent.getNorceCheckoutEnabled && !hasPageTypeConflict"
            :active-variant="activeVariant"
            :product-item="productItem"
            design="standard"
            class="mb-20 desk:mb-24"
          />

          <!-- * * * * * *  new design: order sample or see floor in store -->
          <ExperienceFloorOptions
            v-if="showExperienceOptions"
            :active-variant="activeVariant"
            :product-item="productItem"
            class="mb-20 desk:mb-24"
          />

          <!-- * * * * * *  buy samples -->
          <BuyBoxSamplesOnly
            v-else-if="!hasPageTypeConflict && !globalContent.getIsGlobalMarket"
            :active-variant="activeVariant"
            :product-item="productItem"
            class="mb-20 desk:mb-24"
          />

          <!-- * * * * find retailer -->
          <FindRetailerQuickBox v-if="!showExperienceOptions && !productItem.isD2C && !globalContent.getIsGlobalMarket && !isKarelia" />
        </div>
      </div>
    </div>

    <!-- * * * * * * * Rest of the content -->
    <div class="layout-container">
      <!-- * * * * Quick Jump * * * * -->
      <B2CAnchorLinks v-if="!isLimitedContent" />
      <ProductPersonality
        v-if="!isLimitedContent"
        :active-variant="activeVariant"
        :product-item="productItem"
      />

      <!-- * * * * product accesories -->
      <ProductAccessories
        :active-variant="activeVariant"
        :product-item="productItem"
        design="b2c"
      />
    </div>

    <!-- * * * * mood gallery -->
    <MoodGallery
      v-if="!isLimitedContent"
      :active-variant="activeVariant"
      :product-item="productItem"
    />

    <!-- * * * * generic content -->
    <SpaceHandler v-if="genericContent && genericContent[0] && !isLimitedContent" :blok="genericContent[0]">
      <component :is="genericContent[0].component" :blok="genericContent[0]" />
    </SpaceHandler>

    <!-- * * * * product facts/files -->
    <div class="bg-bg-subtle py-48 desk:py-96 mobOnly:mb-24">
      <div class="max-w-desktopMd mobOnly:px-12 mx-auto">

        <SafetyInformation
          :active-variant="activeVariant"
          :product-item="productItem"
          design="b2c"
        />

        <a id="pdpShowParametrics" />
        <GenericAccordion
          :headline="$t('productPage.productFacts')"
          size="xl"
          start-open
        >
          <ShowParametrics
            :active-variant="activeVariant"
            :product-item="productItem"
            class="mb-32"
          />
        </GenericAccordion>
        <ProductFiles
          :active-variant="activeVariant"
          :product-item="productItem"
          design="b2cLight"
        />
      </div>
    </div>

    <!-- * * * * generic content -->
    <SpaceHandler
      v-if="genericContent && genericContent[1] && !isLimitedContent"
      :blok="genericContent[1]"
    >
      <component :is="genericContent[1].component" :blok="genericContent[1]" />
    </SpaceHandler>

    <FloorPlannerPromo
      v-if="!isLimitedContent"
      :active-variant="activeVariant"
      :product-item="productItem"
    />

    <!-- * * * * generic content -->
    <SpaceHandler
      v-if="genericContent && genericContent[2] && !isLimitedContent"
      :blok="genericContent[2]"
    >
      <component :is="genericContent[2].component" :blok="genericContent[2]" />
    </SpaceHandler>

    <!-- * * * * related products -->

    <div
      class="desk:layout-container"
      :class="{ 'mb-32 desk:mt-64': isLimitedContent }"
    >
      <RelatedProductsList :product-item="productItem" />
    </div>

    <ProductArticles
      v-if="!isLimitedContent"
      :product-item="productItem"
    />

    <!-- the rest of generic content -->
    <template v-if="genericContent && genericContent.length > 3 && !isLimitedContent">
      <SpaceHandler
        v-for="generic in genericContent.slice(2)"
        :key="generic._uid"
        :blok="generic"
      >
        <component :is="generic.component" :blok="generic" />
      </SpaceHandler>
    </template>
  </div>
</template>

<script lang="ts" setup>
import VariantSelector from '~/components/product-page/VariantSelector.vue';
import ProductImage from '~/components/product-page/ProductImage.vue';
import type { ProductModel } from '#models/product';
import type { ProductVariantModel } from '#models/productVariant';
import type { NorceMediaSetCodes } from '#constants/norceCodes';
import ShowParametrics from '~/components/product-page/ShowParametrics.vue';
import GenericAccordion from '~/components/generic/GenericAccordion.vue';
import ProductFiles from '~/components/product-page/ProductFiles.vue';
import ProductBreadcrumbs from '~/components/product-page/ProductBreadcrumbs.vue';
import { useUiStore } from '~/store/ui';
import B2CNameAndCategory from '~/components/product-page/B2CNameAndCategory.vue';
import PricePublic from '~/components/product-page/PricePublic.vue';
import BuyBoxSamplesOnly from '~/components/product-page/BuyBoxSamplesOnly.vue';
import ExperienceFloorOptions from '~/components/product-page/ExperienceFloorOptions.vue';
import { useGlobalContentStore } from '~/store/globalContent';
import useLinkReplacer from '~/composables/useLinkReplacer';
import SpaceHandler from '~/components/body/SpaceHandler.vue';
import ProductAccessories from '~/components/product-page/ProductAccessories.vue';
import MoodGallery from '~/components/product-page/MoodGallery.vue';
import B2CAnchorLinks from '~/components/product-page/B2CAnchorLinks.vue';
import ProductPersonality from '~/components/product-page/ProductPersonality.vue';
import FloorPlannerPromo from '~/components/product-page/FloorPlannerPromo.vue';
import RelatedProductsList from '~/components/product-page/RelatedProductsList.vue';
import ProductArticles from '~/components/product-page/ProductArticles.vue';
import FindRetailerQuickBox from '~/components/product-page/FindRetailerQuickBox.vue';
import BuyBox from '~/components/product-page/BuyBox.vue';
import { computed } from 'vue';
import SafetyInformation from '~/components/product-page/SafetyInformation.vue';

const uiStore = useUiStore();
const storyblokApi = useStoryblokApi();
const globalContent = useGlobalContentStore();
const { replaceLinksInStoryBlokResponse } = useLinkReplacer();

const props = defineProps<{
  productItem: ProductModel,
  activeVariant: ProductVariantModel,
  currentImageList: NorceMediaSetCodes,
}>();
const emit = defineEmits<{
  (e: 'updateActiveVariant', obj: ProductVariantModel): void;
}>();

const updateActiveVariant = async(variant: ProductVariantModel) => {
  emit('updateActiveVariant', variant);
};

const genericContent = ref<any>(null);

const loadGenericContent = async() => {
  // Note that we dont use getConsumerTypeSettingsPath, this is b2c only
  const res = await storyblokApi.get(`cdn/stories/${globalContent.domainMap?.storyblokBasePath}/sitesettings/b2c-pdp-generic-content`, {
    version: useRuntimeConfig().public.storyblokStoriesVersion as 'draft' | 'published',
    resolve_links: 'url',
  }).catch(() => null);
  if (res) {
    genericContent.value = replaceLinksInStoryBlokResponse(res.data.story.content.body);
  }
};

const isKarelia = props.productItem.brand.code?.toUpperCase() === 'KARELIA';
const isLimitedContent = props.productItem.categories.some(s => s.code === 'Accessories') || isKarelia;

// Use B2C-specific display rules regardless of the visiting user's customer type
const b2cDisplayIncVat = computed(() =>
  globalContent.domainMap?.b2c?.displayIncVat ?? globalContent.domainMap?.displayIncVat ?? false
);

const hasRelatedProducts = computed(()=> {
  return props.activeVariant.relatedProducts.length > 0;
});

// B2B user viewing B2C page - hide buy functionality
const hasPageTypeConflict = computed(() => globalContent.getIsB2b);

const showExperienceOptions = computed(() =>
  globalContent.getNewB2CDesign && !hasPageTypeConflict.value && !props.productItem.isD2C && !isLimitedContent
);

onMounted(()=> loadGenericContent());

</script>
